import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Modal, message, Button, Tag } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  DatabaseOutlined,
  UserSwitchOutlined,
  MessageOutlined,
  RobotOutlined,
  BookOutlined,
  ApartmentOutlined,
  BugOutlined,
  ApiOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  SecurityScanOutlined,
  AppstoreOutlined,
  MenuOutlined,
  GiftOutlined,
  DollarOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  VideoCameraOutlined,
  ToolOutlined,
  TrophyOutlined,
  TagsOutlined,
  CrownOutlined,
  PercentageOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  CloudDownloadOutlined,
  FileAddOutlined,
  UserSwitchOutlined as PlatformOutlined,
  DashboardOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import Login from './components/Auth/Login';
import PermissionRoute from './components/Auth/PermissionRoute';
import { User, getAccessiblePages, getRoleDisplayName } from './utils/permissions';

// 页面组件
import MemoryManagement from './pages/MemoryManagement';
import UserProfile from './pages/UserProfile';
import ScriptManagement from './pages/ScriptManagement';
import AgentManagement from './pages/AgentManagement';
import KnowledgeBase from './pages/KnowledgeBase';
import WorkflowManagement from './pages/WorkflowManagement';
import DebugTools from './pages/DebugTools';
import ModelManagement from './pages/ModelManagement';
import Reports from './pages/Reports';
import RetentionReports from './pages/RetentionReports';
import Dashboard from './pages/Dashboard';
import AuditLogs from './pages/AuditLogs';
import SystemSettings from './pages/SystemSettings';
import AccountManagement from './pages/AccountManagement';
import RoleManagement from './pages/RoleManagement';
import MenuManagement from './pages/MenuManagement';
import OperationLogs from './pages/OperationLogs';
import ActivityRewards from './pages/ActivityRewards';
import BalanceLogs from './pages/BalanceLogs';
import LiveRoomUnpaidLogs from './pages/LiveRoomUnpaidLogs';
import DomainManagement from './pages/DomainManagement';
// @ts-ignore
import CardManagement from './pages/CardManagement';
import LiveStreamReport from './pages/LiveStreamReport';
import StyleManagement from './pages/StyleManagement';
import JackpotBroadcastConfig from './pages/JackpotBroadcastConfig';
import LegacyActivityConfig from './pages/LegacyActivityConfig';
import PopupConfig from './pages/PopupConfig';
import UserTagManagement from './pages/UserTagManagement';
import VenueGameManagement from './pages/VenueGameManagement';
import MemberList from './pages/MemberList';
import PhoneNumberExport from './pages/PhoneNumberExport';
import IOSDownloadGuide from './pages/IOSDownloadGuide';
import FunnelReport from './pages/FunnelReport';
import SocialRoleManagement from './pages/SocialRoleManagement';
import SocialScriptManagement from './pages/SocialScriptManagement';
import SocialScriptAnalytics from './pages/SocialScriptAnalytics';
import SocialRoleAnalytics from './pages/SocialRoleAnalytics';
import ContentProductConfig from './pages/ContentProductConfig';
import VipProductConfig from './pages/VipProductConfig';
import ActivityCategoryManagement from './pages/ActivityCategoryManagement';
import ActivityIPDeviceSettings from './pages/ActivityIPDeviceSettings';
import ChannelConfig from './pages/ChannelConfig';
import VipLevelConfig from './pages/VipLevelConfig';
import VipRebateConfig from './pages/VipRebateConfig';
import LiveRoomConfig from './pages/LiveRoomConfig';
import LiveStreamSettings from './pages/LiveStreamSettings';
import AnchorLiveStream from './pages/AnchorLiveStream';
import AgentReportRefactored from './pages/AgentReportRefactored';
import GameDataReport from './pages/GameDataReport';
import BlacklistManagement from './pages/BlacklistManagement';
import ChannelSourceAnalysis from './pages/ChannelSourceAnalysis';
import GameSourceAnalysis from './pages/GameSourceAnalysis';
import ContentTypeConfig from './pages/ai-automation/ContentTypeConfig';
import OutputConfig from './pages/ai-automation/OutputConfig';
import DataSourceStrategy from './pages/ai-automation/DataSourceStrategy';
import ContentGenerationManagement from './pages/ai-automation/ContentGenerationManagement';
import PlatformAccountConfig from './pages/ai-automation/PlatformAccountConfig';
import OperationDataDashboard from './pages/ai-automation/OperationDataDashboard';
import WithdrawalRiskControl from './pages/WithdrawalRiskControl';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const App: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'sidebar' | 'top'>('sidebar');
  const [versionModalVisible, setVersionModalVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 版本信息
  const appVersion = {
    version: '2.3.46',
    buildTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    history: [
        {
      version: '2.3.46',
      date: '2025-12-02',
      features: [
          '新增漏斗模型报表页面，支持多种漏斗分析和对比分析',
          '支持注册、充值、游戏等多种漏斗类型',
          '支持按用户标签区分人群（只玩三方游戏、直播游戏用户等）',
          '支持单漏斗分析和多标签对比分析模式',
          '提供漏斗可视化图表和数据明细表格'
      ],
    },
{
        version: '2.3.46',
        date: dayjs().format('YYYY-MM-DD'),
        features: [
          '新增用户标签管理页面，支持标签组管理和标签管理',
          '标签支持手动标签和自动标签两种类型',
          '自动标签支持配置标记条件和取消标记条件，支持且/或逻辑',
          '标签组支持互斥设置，同一组内标签可设置为互斥',
          '新增场馆游戏管理页面，支持游戏列表、新增、编辑、删除功能',
          '场馆游戏管理支持子游戏映射功能，可配置标签范围和映射比例',
          '子游戏映射支持选择目标场馆和目标子游戏，支持标签逻辑（且/或）配置',
    ],
      },
      {
        version: '2.3.46',
        date: '2025-01-27',
        features: [
          '新增大奖播报配置页面，支持基础设置、用户黑名单、游戏黑名单和数据明细管理',
          '游戏黑名单支持两级结构：游戏场馆和子游戏，支持级联选择',
          '数据明细展示所有中奖数据，包括游戏场馆、游戏类型、过滤状态等信息',
          '支持手动控制每条记录的展示/不展示状态，支持批量操作',
        ],
      },
      {
        version: '2.3.46',
        date: '2025-11-01',
        features: [
          '直播报表优化：改为单条聚合数据展示，统计日期显示为时间范围',
          '直播报表新增查询条件：主播ID、游戏类型、直播间ID',
          '直播报表新增游戏统计功能，支持按游戏类型查看统计数据',
          '优化直播报表数据展示，操作列增加游戏统计按钮',
        ],
      },
      {
        version: '2.3.46',
        date: '2025-10-31',
        features: [
          '新增直播报表页面，支持总表、主播明细和场次详情三级数据查看',
          '新增风格管理页面，支持主/副风格分类和权重配置',
          '智能体管理重构：移除能力字段，改为领域配置和风格配置',
          '知识库管理优化：新增二级分类和知识等级配置',
          '话术管理优化：将标签改为风格关联，统一字段命名',
        ],
      },
      {
        version: '2.3.46',
        date: '2025-01-27',
        features: [
          '新增卡片管理功能，支持8种卡片类型配置',
          '新增留存报表页面',
          '优化智能体管理和领域配置界面',
          '完善域名管理功能',
          '优化界面布局和用户体验',
        ],
      },
    ],
  };

  const handleLogin = (userData: User) => {
    setUser(userData);
    setSessionTimeout(false);
    message.success(`欢迎回来，${userData.name}！`);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    setUser(null);
    navigate('/');
    message.info('已退出登录');
  };

  // 检查登录状态
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const loginTime = localStorage.getItem('loginTime');
    
    if (savedUser && loginTime) {
      const userData = JSON.parse(savedUser);
      const timeDiff = Date.now() - parseInt(loginTime);
      
      // 检查会话是否超时（2小时）
      if (timeDiff > 2 * 60 * 60 * 1000) {
        handleLogout();
        setSessionTimeout(true);
      } else {
        setUser(userData);
      }
    }
  }, []);

  // 会话超时检查
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime) {
        const timeDiff = Date.now() - parseInt(loginTime);
        if (timeDiff > 2 * 60 * 60 * 1000) {
          handleLogout();
          setSessionTimeout(true);
        }
      }
    }, 60000); // 每分钟检查一次
    
    return () => clearInterval(interval);
  }, [user]);

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      Modal.confirm({
        title: '确认退出',
        icon: <ExclamationCircleOutlined />,
        content: '确定要退出登录吗？',
        onOk: handleLogout,
      });
    } else if (key === 'profile') {
      message.info('个人设置功能开发中...');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  // 公开页面（不需要登录）
  const publicPaths = ['/ios-download-guide'];
  const isPublicPath = publicPaths.includes(location.pathname);
  
  // 如果是公开页面，直接渲染，不需要登录
  if (isPublicPath) {
    return (
      <Layout style={{ minHeight: '100vh', background: 'transparent' }}>
        <Routes>
          <Route path="/ios-download-guide" element={<IOSDownloadGuide />} />
        </Routes>
      </Layout>
    );
  }

  // 如果没有登录，显示登录页面
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // 获取用户可访问的页面
  const accessiblePages = getAccessiblePages(user);
  
  const allMenuItems = [
    {
      key: '/dashboard',
      icon: <BarChartOutlined />,
      label: '仪表盘',
    },
    {
      key: 'business',
      icon: <RobotOutlined />,
      label: '业务管理',
      children: [
        {
          key: '/memory',
          icon: <DatabaseOutlined />,
          label: '记忆管理',
        },
        {
          key: '/profile',
          icon: <UserSwitchOutlined />,
          label: '用户画像',
        },
        {
          key: '/script',
          icon: <MessageOutlined />,
          label: '话术管理',
        },
        {
          key: '/agent',
          icon: <RobotOutlined />,
          label: '智能体管理',
        },
        {
          key: '/knowledge',
          icon: <BookOutlined />,
          label: '知识库管理',
        },
        {
          key: '/style',
          icon: <ToolOutlined />,
          label: '风格管理',
        },
        {
          key: '/workflow',
          icon: <ApartmentOutlined />,
          label: '工作流管理',
        },
      ]
    },
    {
      key: 'operation',
      icon: <BugOutlined />,
      label: '运营管理',
      children: [
        {
          key: '/debug',
          icon: <BugOutlined />,
          label: '调试工具',
        },
        {
          key: '/model',
          icon: <ApiOutlined />,
          label: '大模型管理',
        },
        {
          key: '/reports',
          icon: <BarChartOutlined />,
          label: '数据报表',
        },
      ]
    },
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/audit-logs',
          icon: <SecurityScanOutlined />,
          label: '审计日志',
        },
        {
          key: '/operation-logs',
          icon: <ExclamationCircleOutlined />,
          label: '操作日志',
        },
        {
          key: '/account',
          icon: <UserOutlined />,
          label: '账号管理',
        },
        {
          key: '/role',
          icon: <SecurityScanOutlined />,
          label: '角色管理',
        },
        {
          key: '/menu',
          icon: <MenuOutlined />,
          label: '菜单管理',
        },
        {
          key: '/settings',
          icon: <SettingOutlined />,
          label: '系统设置',
        },
      ]
    },
    {
      key: 'ai-automation',
      icon: <ThunderboltOutlined />,
      label: 'AI自动化配置系统',
      children: [
        {
          key: '/ai-automation/content-type-config',
          icon: <FileTextOutlined />,
          label: '内容类型配置',
        },
        {
          key: '/ai-automation/output-config',
          icon: <FileAddOutlined />,
          label: '输出物配置',
        },
        {
          key: '/ai-automation/data-source-strategy',
          icon: <CloudDownloadOutlined />,
          label: '数据源策略配置',
        },
        {
          key: '/ai-automation/content-generation',
          icon: <FileAddOutlined />,
          label: '内容生成管理',
        },
        {
          key: '/ai-automation/platform-account-config',
          icon: <PlatformOutlined />,
          label: '平台账号配置',
        },
        {
          key: '/ai-automation/operation-data',
          icon: <DashboardOutlined />,
          label: '运营数据展示',
        },
      ]
    },
    {
      key: 'social-backend',
      icon: <TeamOutlined />,
      label: '社交后台',
      children: [
        {
          key: '/social-roles',
          icon: <UserOutlined />,
          label: '女主角色管理',
        },
        {
          key: '/social-role-analytics',
          icon: <BarChartOutlined />,
          label: '女主角色统计',
        },
        {
          key: '/social-scripts',
          icon: <FileTextOutlined />,
          label: '剧本管理',
        },
        {
          key: '/social-script-analytics',
          icon: <BarChartOutlined />,
          label: '剧本统计',
        },
      ],
    },
    {
      key: 'content-backend',
      icon: <AppstoreOutlined />,
      label: '内容产品后台',
      children: [
        {
          key: '/content-config',
          icon: <SettingOutlined />,
          label: '内容配置页面',
        },
        {
          key: '/vip-config',
          icon: <CrownOutlined />,
          label: 'VIP配置',
        },
      ],
    },
    {
      key: 'legacy',
      icon: <DatabaseOutlined />,
      label: '老平台后台',
      children: [
        {
          key: '/domain',
          icon: <GlobalOutlined />,
          label: '代理绑定管理',
        },
        {
          key: '/activity-rewards',
          icon: <GiftOutlined />,
          label: '活动奖励记录',
        },
        {
          key: '/balance-logs',
          icon: <DollarOutlined />,
          label: '资金日志',
        },
        {
          key: '/withdrawal-risk-control',
          icon: <SecurityScanOutlined />,
          label: '提现风控',
        },
        {
          key: '/live-room-unpaid-logs',
          icon: <VideoCameraOutlined />,
          label: '直播未付费日志',
        },
        {
          key: '/card-config',
          icon: <AppstoreOutlined />,
          label: '卡片管理',
        },
        {
          key: '/retention-report',
          icon: <BarChartOutlined />,
          label: '留存报表',
        },
        {
          key: '/live-stream-report',
          icon: <VideoCameraOutlined />,
          label: '直播报表',
        },
        {
          key: '/jackpot-broadcast',
          icon: <TrophyOutlined />,
          label: '大奖播报配置',
        },
        {
          key: '/activity-config',
          icon: <GiftOutlined />,
          label: '活动配置',
        },
        {
          key: '/activity-category-management',
          icon: <AppstoreOutlined />,
          label: '活动大类配置',
        },
        {
          key: '/activity-ip-device-settings',
          icon: <SecurityScanOutlined />,
          label: '活动ip/设备设置',
        },
        {
          key: '/channel-config',
          icon: <GlobalOutlined />,
          label: '渠道配置',
        },
        {
          key: '/popup-config',
          icon: <InfoCircleOutlined />,
          label: '弹窗配置',
        },
        {
          key: '/user-tag-management',
          icon: <TagsOutlined />,
          label: '用户标签管理',
        },
        {
          key: '/venue-game-management',
          icon: <AppstoreOutlined />,
          label: '场馆游戏管理',
        },
        {
          key: '/member-list',
          icon: <UserOutlined />,
          label: '会员列表',
        },
        {
          key: '/phone-number-export',
          icon: <CloudDownloadOutlined />,
          label: '手机号码导出',
        },
        {
          key: '/funnel-report',
          icon: <BarChartOutlined />,
          label: '漏斗模型报表',
        },
        {
          key: '/vip-level-config',
          icon: <CrownOutlined />,
          label: 'VIP等级配置',
        },
        {
          key: '/vip-rebate-config',
          icon: <PercentageOutlined />,
          label: 'VIP返水配置',
        },
        {
          key: '/live-room-config',
          icon: <VideoCameraOutlined />,
          label: '直播间配置',
        },
        {
          key: '/live-stream-settings',
          icon: <VideoCameraOutlined />,
          label: '直播设置',
        },
        {
          key: '/anchor-live-stream',
          icon: <VideoCameraOutlined />,
          label: '开始直播',
        },
        {
          key: '/agent-report-refactored',
          icon: <BarChartOutlined />,
          label: '代理报表(重构)',
        },
        {
          key: '/game-data-report',
          icon: <BarChartOutlined />,
          label: '游戏数据报表',
        },
        {
          key: '/blacklist-management',
          icon: <SecurityScanOutlined />,
          label: '黑名单管理',
        },
        {
          key: '/channel-source-analysis',
          icon: <BarChartOutlined />,
          label: '渠道来源分析',
        },
        {
          key: '/game-source-analysis',
          icon: <BarChartOutlined />,
          label: '游戏来源分析',
        },
      ]
    },
  ];

  // 根据权限过滤菜单
  const filterMenuItems = (items: any[]): any[] => {
    // 定义为免权限检查的页面
    const openPages = ['social-roles', 'social-role-analytics', 'social-scripts', 'social-script-analytics', 'content-config', 'vip-config', 'domain', 'activity-rewards', 'balance-logs', 'live-room-unpaid-logs', 'card-config', 'retention-report', 'live-stream-report', 'jackpot-broadcast', 'activity-config', 'activity-category-management', 'activity-ip-device-settings', 'channel-config', 'popup-config', 'user-tag-management', 'venue-game-management', 'member-list', 'phone-number-export', 'funnel-report', 'vip-level-config', 'vip-rebate-config', 'live-room-config', 'live-stream-settings', 'anchor-live-stream', 'agent-report-refactored', 'game-data-report', 'blacklist-management', 'channel-source-analysis', 'game-source-analysis'];

    const isPageAllowed = (pagePath: string) => {
      if (pagePath.startsWith('ai-automation/')) {
        const page = pagePath.replace('ai-automation/', '');
        return accessiblePages.includes(page);
      }
      if (openPages.includes(pagePath)) {
        return true;
      }
      return accessiblePages.includes(pagePath);
    };
    
    return items
      .map((item) => {
        if (item.children) {
          const filteredChildren = filterMenuItems(item.children);
          if (filteredChildren.length === 0) {
            return null;
          }
          return {
            ...item,
            children: filteredChildren,
          };
        }
        const key = typeof item.key === 'string' ? item.key : '';
        if (!key.startsWith('/')) {
          return item;
        }
        const pagePath = key.substring(1);
        return isPageAllowed(pagePath) ? item : null;
      })
      .filter(Boolean);
  };

  const menuItems = filterMenuItems(allMenuItems);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人设置',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLayoutToggle = () => {
    setLayoutMode(prev => prev === 'sidebar' ? 'top' : 'sidebar');
    message.info(`已切换到${layoutMode === 'sidebar' ? '顶部导航' : '侧边栏'}模式`);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {layoutMode === 'sidebar' ? (
        <>
          <Sider trigger={null} collapsible collapsed={collapsed} style={{ overflow: 'hidden' }}>
            <div style={{ 
              height: '100vh', 
              display: 'flex', 
              flexDirection: 'column',
              background: '#001529'
            }}>
              <div style={{ 
                height: 32, 
                margin: 16, 
                background: 'rgba(255, 255, 255, 0.2)', 
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {collapsed ? 'AI' : 'AI机器人系统'}
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden'
              }}>
                <Menu
                  theme="dark"
                  mode="inline"
                  selectedKeys={[location.pathname]}
                  items={menuItems}
                  onClick={handleMenuClick}
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          </Sider>
          <Layout>
            <Header style={{ padding: 0, background: '#fff' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                height: '100%',
                padding: '0 24px'
              }}>
                <div>
                  {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                    className: 'trigger',
                    onClick: () => setCollapsed(!collapsed),
                    style: { fontSize: '18px', cursor: 'pointer' }
                  })}
                </div>
                <div>
                  <Space>
                    <Button
                      icon={layoutMode === 'sidebar' ? <AppstoreOutlined /> : <MenuOutlined />}
                      onClick={handleLayoutToggle}
                      title="切换布局"
                    />
                    <Text 
                      type="secondary" 
                      style={{ cursor: 'pointer', fontSize: '12px' }}
                      onClick={() => setVersionModalVisible(true)}
                    >
                      v{appVersion.version}
                    </Text>
                    <Dropdown
                      menu={{
                        items: userMenuItems,
                        onClick: handleUserMenuClick,
                      }}
                      placement="bottomRight"
                    >
                      <Space style={{ cursor: 'pointer' }}>
                        <Avatar src={user.avatar} icon={<UserOutlined />} />
                        <Text strong>{user.name}</Text>
                      </Space>
                    </Dropdown>
                  </Space>
                </div>
              </div>
            </Header>
            <Content style={{ 
              height: '100vh', 
              overflowY: 'auto', 
              overflowX: 'hidden',
              background: '#f0f2f5'
            }}>
          <Routes>
            <Route path="/" element={
              <PermissionRoute user={user} page="dashboard" action="view">
                <Dashboard />
              </PermissionRoute>
            } />
            <Route path="/dashboard" element={
              <PermissionRoute user={user} page="dashboard" action="view">
                <Dashboard />
              </PermissionRoute>
            } />
            <Route path="/memory" element={
              <PermissionRoute user={user} page="memory" action="view">
                <MemoryManagement />
              </PermissionRoute>
            } />
            <Route path="/profile" element={
              <PermissionRoute user={user} page="profile" action="view">
                <UserProfile />
              </PermissionRoute>
            } />
            <Route path="/script" element={
              <PermissionRoute user={user} page="script" action="view">
                <ScriptManagement />
              </PermissionRoute>
            } />
            <Route path="/agent" element={
              <PermissionRoute user={user} page="agent" action="view">
                <AgentManagement />
              </PermissionRoute>
            } />
            <Route path="/knowledge" element={
              <PermissionRoute user={user} page="knowledge" action="view">
                <KnowledgeBase />
              </PermissionRoute>
            } />
            <Route path="/style" element={
              <PermissionRoute user={user} page="style" action="view">
                <StyleManagement />
              </PermissionRoute>
            } />
            <Route path="/workflow" element={
              <PermissionRoute user={user} page="workflow" action="view">
                <WorkflowManagement />
              </PermissionRoute>
            } />
            <Route path="/debug" element={
              <PermissionRoute user={user} page="debug" action="view">
                <DebugTools />
              </PermissionRoute>
            } />
            <Route path="/model" element={
              <PermissionRoute user={user} page="model" action="view">
                <ModelManagement />
              </PermissionRoute>
            } />
            <Route path="/reports" element={
              <PermissionRoute user={user} page="reports" action="view">
                <Reports />
              </PermissionRoute>
            } />
            <Route path="/audit-logs" element={
              <PermissionRoute user={user} page="audit" action="view">
                <AuditLogs />
              </PermissionRoute>
            } />
            <Route path="/operation-logs" element={
              <PermissionRoute user={user} page="audit" action="view">
                <OperationLogs />
              </PermissionRoute>
            } />
            <Route path="/account" element={
              <PermissionRoute user={user} page="account" action="view">
                <AccountManagement />
              </PermissionRoute>
            } />
            <Route path="/role" element={
              <PermissionRoute user={user} page="role" action="view">
                <RoleManagement />
              </PermissionRoute>
            } />
            <Route path="/menu" element={
              <PermissionRoute user={user} page="menu" action="view">
                <MenuManagement />
              </PermissionRoute>
            } />
            <Route path="/settings" element={
              <PermissionRoute user={user} page="settings" action="view">
                <SystemSettings />
              </PermissionRoute>
            } />
            <Route path="/social-roles" element={
              <SocialRoleManagement />
            } />
            <Route path="/social-role-analytics" element={
              <SocialRoleAnalytics />
            } />
            <Route path="/social-scripts" element={
              <SocialScriptManagement />
            } />
            <Route path="/social-script-analytics" element={
              <SocialScriptAnalytics />
            } />
            <Route path="/content-config" element={
              <ContentProductConfig />
            } />
            <Route path="/vip-config" element={
              <VipProductConfig />
            } />
            <Route path="/domain" element={
              <DomainManagement />
            } />
            <Route path="/activity-rewards" element={
              <ActivityRewards />
            } />
            <Route path="/balance-logs" element={
              <BalanceLogs />
            } />
            <Route path="/withdrawal-risk-control" element={
              <PermissionRoute user={user} page="withdrawal-risk-control" action="view">
                <WithdrawalRiskControl />
              </PermissionRoute>
            } />
            <Route path="/live-room-unpaid-logs" element={
              <LiveRoomUnpaidLogs />
            } />
            <Route path="/card-config" element={
              <CardManagement />
            } />
            <Route path="/retention-report" element={
              <RetentionReports />
            } />
            <Route path="/live-stream-report" element={
              <LiveStreamReport />
            } />
            <Route path="/jackpot-broadcast" element={
              <JackpotBroadcastConfig />
            } />
            <Route path="/activity-config" element={
              <LegacyActivityConfig />
            } />
            <Route path="/activity-category-management" element={
              <ActivityCategoryManagement />
            } />
            <Route path="/activity-ip-device-settings" element={
              <ActivityIPDeviceSettings />
            } />
            <Route path="/channel-config" element={
              <ChannelConfig />
            } />
            <Route path="/popup-config" element={
              <PopupConfig />
            } />
            <Route path="/user-tag-management" element={
              <UserTagManagement />
            } />
            <Route path="/venue-game-management" element={
              <VenueGameManagement />
            } />
            <Route path="/member-list" element={
              <MemberList />
            } />
            <Route path="/phone-number-export" element={
              <PhoneNumberExport />
            } />
            <Route path="/funnel-report" element={
              <FunnelReport />
            } />
            <Route path="/vip-level-config" element={
              <VipLevelConfig />
            } />
            <Route path="/vip-rebate-config" element={
              <VipRebateConfig />
            } />
            <Route path="/live-room-config" element={
              <LiveRoomConfig />
            } />
            <Route path="/live-stream-settings" element={
              <LiveStreamSettings />
            } />
            <Route path="/anchor-live-stream" element={
              <AnchorLiveStream />
            } />
            <Route path="/agent-report-refactored" element={
              <AgentReportRefactored />
            } />
            <Route path="/game-data-report" element={
              <GameDataReport />
            } />
            <Route path="/blacklist-management" element={
              <BlacklistManagement />
            } />
            <Route path="/channel-source-analysis" element={
              <ChannelSourceAnalysis />
            } />
            <Route path="/game-source-analysis" element={
              <GameSourceAnalysis />
            } />
            <Route path="/ai-automation/content-type-config" element={
              <PermissionRoute user={user} page="content-type-config" action="view">
                <ContentTypeConfig />
              </PermissionRoute>
            } />
            <Route path="/ai-automation/output-config" element={
              <PermissionRoute user={user} page="output-config" action="view">
                <OutputConfig />
              </PermissionRoute>
            } />
            <Route path="/ai-automation/data-source-strategy" element={
              <PermissionRoute user={user} page="data-source-strategy" action="view">
                <DataSourceStrategy />
              </PermissionRoute>
            } />
            <Route path="/ai-automation/content-generation" element={
              <PermissionRoute user={user} page="content-generation" action="view">
                <ContentGenerationManagement />
              </PermissionRoute>
            } />
            <Route path="/ai-automation/platform-account-config" element={
              <PermissionRoute user={user} page="platform-account-config" action="view">
                <PlatformAccountConfig />
              </PermissionRoute>
            } />
            <Route path="/ai-automation/operation-data" element={
              <PermissionRoute user={user} page="operation-data" action="view">
                <OperationDataDashboard />
              </PermissionRoute>
            } />
              <Route path="/activity-config" element={
                <LegacyActivityConfig />
              } />
          </Routes>
            </Content>
          </Layout>
        </>
      ) : (
        // 顶部导航布局
        <Layout>
          <Header style={{ 
            padding: 0, 
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '100%',
              padding: '0 24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  marginRight: 24
                }}>
                  AI机器人系统
                </div>
                <Menu
                  mode="horizontal"
                  selectedKeys={[location.pathname]}
                  items={menuItems}
                  onClick={handleMenuClick}
                  style={{ border: 'none', background: 'transparent' }}
                />
              </div>
              <div>
                <Space>
                  <Button
                    icon={<MenuOutlined />}
                    onClick={handleLayoutToggle}
                    title="切换到侧边栏模式"
                  />
                  <Text 
                    type="secondary" 
                    style={{ cursor: 'pointer', fontSize: '12px' }}
                    onClick={() => setVersionModalVisible(true)}
                  >
                    v{appVersion.version}
                  </Text>
                  <Dropdown
                    menu={{
                      items: userMenuItems,
                      onClick: handleUserMenuClick,
                    }}
                    placement="bottomRight"
                  >
                    <Space style={{ cursor: 'pointer' }}>
                      <Avatar src={user.avatar} icon={<UserOutlined />} />
                      <Text strong>{user.name}</Text>
                    </Space>
                  </Dropdown>
                </Space>
              </div>
            </div>
          </Header>
          <Content style={{ 
            height: 'calc(100vh - 64px)', 
            overflowY: 'auto', 
            overflowX: 'hidden',
            background: '#f0f2f5'
          }}>
            <Routes>
              <Route path="/" element={
                <PermissionRoute user={user} page="dashboard" action="view">
                  <Dashboard />
                </PermissionRoute>
              } />
              <Route path="/dashboard" element={
                <PermissionRoute user={user} page="dashboard" action="view">
                  <Dashboard />
                </PermissionRoute>
              } />
              <Route path="/memory" element={
                <PermissionRoute user={user} page="memory" action="view">
                  <MemoryManagement />
                </PermissionRoute>
              } />
              <Route path="/profile" element={
                <PermissionRoute user={user} page="profile" action="view">
                  <UserProfile />
                </PermissionRoute>
              } />
              <Route path="/script" element={
                <PermissionRoute user={user} page="script" action="view">
                  <ScriptManagement />
                </PermissionRoute>
              } />
              <Route path="/agent" element={
                <PermissionRoute user={user} page="agent" action="view">
                  <AgentManagement />
                </PermissionRoute>
              } />
              <Route path="/knowledge" element={
                <PermissionRoute user={user} page="knowledge" action="view">
                  <KnowledgeBase />
                </PermissionRoute>
              } />
              <Route path="/style" element={
                <PermissionRoute user={user} page="style" action="view">
                  <StyleManagement />
                </PermissionRoute>
              } />
              <Route path="/workflow" element={
                <PermissionRoute user={user} page="workflow" action="view">
                  <WorkflowManagement />
                </PermissionRoute>
              } />
              <Route path="/debug" element={
                <PermissionRoute user={user} page="debug" action="view">
                  <DebugTools />
                </PermissionRoute>
              } />
              <Route path="/model" element={
                <PermissionRoute user={user} page="model" action="view">
                  <ModelManagement />
                </PermissionRoute>
              } />
              <Route path="/reports" element={
                <PermissionRoute user={user} page="reports" action="view">
                  <Reports />
                </PermissionRoute>
              } />
              <Route path="/audit-logs" element={
                <PermissionRoute user={user} page="audit" action="view">
                  <AuditLogs />
                </PermissionRoute>
              } />
              <Route path="/operation-logs" element={
                <PermissionRoute user={user} page="audit" action="view">
                  <OperationLogs />
                </PermissionRoute>
              } />
              <Route path="/account" element={
                <PermissionRoute user={user} page="account" action="view">
                  <AccountManagement />
                </PermissionRoute>
              } />
              <Route path="/role" element={
                <PermissionRoute user={user} page="role" action="view">
                  <RoleManagement />
                </PermissionRoute>
              } />
              <Route path="/menu" element={
                <PermissionRoute user={user} page="menu" action="view">
                  <MenuManagement />
                </PermissionRoute>
              } />
              <Route path="/settings" element={
                <PermissionRoute user={user} page="settings" action="view">
                  <SystemSettings />
                </PermissionRoute>
              } />
              <Route path="/social-roles" element={
                <SocialRoleManagement />
              } />
              <Route path="/social-role-analytics" element={
                <SocialRoleAnalytics />
              } />
              <Route path="/social-scripts" element={
                <SocialScriptManagement />
              } />
              <Route path="/social-script-analytics" element={
                <SocialScriptAnalytics />
              } />
              <Route path="/content-config" element={
                <ContentProductConfig />
              } />
              <Route path="/vip-config" element={
                <VipProductConfig />
              } />
              <Route path="/domain" element={
                <DomainManagement />
              } />
              <Route path="/activity-rewards" element={
                <ActivityRewards />
              } />
              <Route path="/balance-logs" element={
                <BalanceLogs />
              } />
              <Route path="/live-room-unpaid-logs" element={
                <LiveRoomUnpaidLogs />
              } />
              <Route path="/card-config" element={
                <CardManagement />
              } />
              <Route path="/live-stream-report" element={
                <LiveStreamReport />
              } />
              <Route path="/ios-download-guide" element={
                <IOSDownloadGuide />
              } />
              <Route path="/live-room-config" element={
                <LiveRoomConfig />
              } />
              <Route path="/agent-report-refactored" element={
                <AgentReportRefactored />
              } />
              <Route path="/game-data-report" element={
                <GameDataReport />
              } />
              <Route path="/blacklist-management" element={
                <BlacklistManagement />
              } />
              <Route path="/channel-source-analysis" element={
                <ChannelSourceAnalysis />
              } />
              <Route path="/game-source-analysis" element={
                <GameSourceAnalysis />
              } />
            </Routes>
          </Content>
        </Layout>
      )}
      
      {/* 版本信息弹窗 */}
      <Modal
        title={<><InfoCircleOutlined /> 版本信息</>}
        open={versionModalVisible}
        onCancel={() => setVersionModalVisible(false)}
        footer={[<Button key="close" onClick={() => setVersionModalVisible(false)}>关闭</Button>]}
        width={600}
      >
        <div style={{ marginBottom: 24 }}>
          <Space size="large" style={{ marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>v{appVersion.version}</div>
              <div style={{ fontSize: 12, color: '#999' }}>构建时间：{appVersion.buildTime}</div>
            </div>
          </Space>
        </div>
        
        <div>
          <h4 style={{ marginBottom: 12 }}>更新历史</h4>
          {appVersion.history.map((item, index) => (
            <div key={index} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: index < appVersion.history.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Tag color="blue" style={{ fontSize: 14, fontWeight: 'bold' }}>v{item.version}</Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>{item.date}</Text>
              </div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {item.features.map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: 4, color: '#666' }}>{feature}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Modal>
    </Layout>
  );
};

export default App;